import React, { useState, useEffect } from 'react';
import cambioEstadoService from "../../services/cambioEstadoService";
import { obtenerSiguienteEstadoART } from "../../services/art/artEstadoService";
import EventoService from '../../services/eventoService';
import feriadoService from '../../services/feriadoService';
import { calcularDiasHabiles, formatDateISO } from '../../utils/fechasUtils';
import { toast } from "react-toastify";
import GavelIcon from '@mui/icons-material/Gavel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import EventIcon from '@mui/icons-material/Event';
import SendIcon from '@mui/icons-material/Send';

const formatFechaLocal = (fecha) => {
    if (!fecha) return '';
    const d = new Date(fecha + 'T00:00:00');
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function GestorResolucionSRT({ tramite, onAvanzar }) {
    const [cargando, setCargando] = useState(false);
    const [fechaNotificacion, setFechaNotificacion] = useState('');
    const [observaciones, setObservaciones] = useState('');
    const [fechaVencimiento, setFechaVencimiento] = useState(null);
    const [cargandoVencimiento, setCargandoVencimiento] = useState(false);
    const [advertenciaFeriados, setAdvertenciaFeriados] = useState([]);
    const [verificandoFeriados, setVerificandoFeriados] = useState(false);

    // Recalcular fecha de vencimiento usando días hábiles (excluyendo fines de semana y feriados)
    useEffect(() => {
        if (!fechaNotificacion) {
            setFechaVencimiento(null);
            return;
        }

        let cancelado = false;
        setCargandoVencimiento(true);

        const calcular = async () => {
            try {
                const fechaInicio = new Date(fechaNotificacion + 'T00:00:00');
                const resultado = await calcularDiasHabiles(fechaInicio, 45);
                if (!cancelado) {
                    setFechaVencimiento(formatDateISO(resultado));
                }
            } catch (error) {
                console.error('Error al calcular días hábiles:', error);
                if (!cancelado) {
                    // Fallback a días corridos si falla el cálculo
                    const d = new Date(fechaNotificacion + 'T00:00:00');
                    d.setDate(d.getDate() + 45);
                    setFechaVencimiento(d.toISOString().split('T')[0]);
                }
            } finally {
                if (!cancelado) setCargandoVencimiento(false);
            }
        };

        calcular();
        return () => { cancelado = true; };
    }, [fechaNotificacion]);

    useEffect(() => {
        if (!fechaNotificacion || !fechaVencimiento) {
            setAdvertenciaFeriados([]);
            return;
        }

        const anioInicio = new Date(fechaNotificacion + 'T00:00:00').getFullYear();
        const anioFin = new Date(fechaVencimiento + 'T00:00:00').getFullYear();
        const anios = anioInicio === anioFin ? [anioInicio] : [anioInicio, anioFin];

        let cancelado = false;
        setVerificandoFeriados(true);

        const verificar = async () => {
            const problemas = [];
            try {
                for (const anio of anios) {
                    const response = await feriadoService.obtenerPorAnio(anio);
                    const feriados = Array.isArray(response?.data) ? response.data : [];

                    const tieneNacionales = feriados.some(f =>
                        ['NACIONAL', 'TRASLADABLE', 'PUENTE', 'NO_LABORABLE'].includes((f.tipo || '').toUpperCase())
                    );

                    if (!tieneNacionales) problemas.push({ anio, tipo: 'nacionales' });
                }
            } catch (e) {
                // Si falla la consulta no bloqueamos al usuario
            }
            if (!cancelado) {
                setAdvertenciaFeriados(problemas);
                setVerificandoFeriados(false);
            }
        };

        verificar();
        return () => { cancelado = true; };
    }, [fechaVencimiento, fechaNotificacion]);

    const crearEventoVencimiento = async (fechaVenc) => {
        const nombreCliente = tramite.cliente
            ? `${tramite.cliente.apellido || ''}, ${tramite.cliente.nombre || ''}`.trim().replace(/^,\s*/, '')
            : `Trámite #${tramite.idTramite}`;

        await EventoService.crearEvento({
            titulo: 'VENCIMIENTO SRT (45 Días)',
            descripcion: `${nombreCliente} — Plazo de 45 días hábiles desde la notificación de la Resolución SRT (${formatFechaLocal(fechaNotificacion)}). Fecha límite para presentar la apelación/telegrama.`,
            fecha: fechaVenc,
            tipoEvento: 'Vencimiento',
            todoElDia: true,
            visibilidad: 'POR_AREA',
            idTramite: tramite.idTramite
        });
    };

    const handleAvanzar = async (estadoDestino) => {
        if (!fechaNotificacion) {
            toast.error('Debe registrar la fecha de notificación de la resolución SRT.');
            return;
        }
        if (!fechaVencimiento) {
            toast.error('Espere mientras se calcula la fecha de vencimiento.');
            return;
        }

        setCargando(true);
        try {
            const siguienteEstado = await obtenerSiguienteEstadoART(tramite.nombreEstadoActual, estadoDestino);

            if (!siguienteEstado) {
                toast.error(`No se pudo obtener el estado destino: ${estadoDestino}.`);
                return;
            }

            // El evento de vencimiento solo se crea cuando el trámite va a juicio
            if (estadoDestino === 'Judicializado') {
                await crearEventoVencimiento(fechaVencimiento);
            }

            const lineasComentario = [
                `Resolución SRT registrada.`,
                `Fecha de notificación: ${formatFechaLocal(fechaNotificacion)}`,
                estadoDestino === 'Judicializado'
                    ? `Fecha límite para judicializar: ${formatFechaLocal(fechaVencimiento)} (45 días hábiles)`
                    : `Todos los siniestros resultaron favorables — trámite finalizado sin juicio.`,
                observaciones.trim() ? `Observaciones: ${observaciones.trim()}` : null
            ].filter(Boolean).join('\n');

            await cambioEstadoService.cambiarEstado(tramite.idTramite, {
                idEstado: siguienteEstado.id || siguienteEstado.idEstado,
                comentario: lineasComentario
            });

            toast.success(`Resolución SRT registrada. Avanzando a ${estadoDestino}.`);
            onAvanzar();
        } catch (error) {
            console.error('Error al registrar Resolución SRT:', error);
            toast.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setCargando(false);
        }
    };

    const puedeAvanzar = !cargando && !!fechaNotificacion && !!fechaVencimiento && !cargandoVencimiento;

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-dark mb-0">
                    <GavelIcon className="me-2" style={{ fontSize: '1.5rem' }} />
                    Resolución SRT
                </h4>
                <span className="badge bg-dark rounded-pill px-3 py-2">
                    Superintendencia
                </span>
            </div>

            {/* Alerta plazo crítico */}
            <div className="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-start gap-3">
                <WarningAmberIcon className="text-warning mt-1 flex-shrink-0" />
                <div>
                    <strong>Plazo crítico: 45 días hábiles</strong>
                    <p className="mb-0 mt-1 small">
                        Desde la fecha de notificación de la resolución SRT, el estudio tiene
                        <strong> 45 días hábiles</strong> para presentar la demanda judicial.
                        Si hay siniestros desfavorables, se creará automáticamente un evento de vencimiento en la agenda.
                    </p>
                </div>
            </div>

            {/* Fecha de notificación */}
            <div className="mb-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <EventIcon fontSize="small" className="me-2 mb-1" />
                    Fecha de Notificación de la Resolución SRT <span className="text-danger">*</span>
                </h6>
                <input
                    type="date"
                    className="form-control"
                    value={fechaNotificacion}
                    onChange={(e) => setFechaNotificacion(e.target.value)}
                    disabled={cargando}
                    style={{ maxWidth: '280px', borderRadius: '0.5rem' }}
                />
                <small className="text-muted mt-2 d-block">
                    La fecha de vencimiento (notificación + 45 días hábiles, excluyendo feriados) se calculará automáticamente.
                </small>
            </div>

            {/* Fecha de vencimiento calculada */}
            {fechaNotificacion && (
                <div
                    className="p-3 rounded-3 mb-4"
                    style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <WarningAmberIcon className="text-warning" />
                        <div>
                            <strong>Fecha límite para judicializar:</strong>{' '}
                            {cargandoVencimiento ? (
                                <span className="spinner-border spinner-border-sm text-warning ms-2" role="status" />
                            ) : (
                                <span className="fw-bold" style={{ fontSize: '1.1rem' }}>
                                    {formatFechaLocal(fechaVencimiento)}
                                </span>
                            )}
                        </div>
                    </div>
                    <small className="text-muted mt-1 d-block">
                        Calculado en días hábiles (sin fines de semana ni feriados registrados en el sistema).
                        Si hay siniestros desfavorables, se creará un evento en la agenda con esta fecha.
                    </small>
                </div>
            )}

            {/* Advertencia feriados incompletos */}
            {!verificandoFeriados && advertenciaFeriados.length > 0 && (
                <div className="alert alert-danger border-0 rounded-3 mb-4 d-flex align-items-start gap-3">
                    <ErrorOutlineIcon className="text-danger mt-1 flex-shrink-0" />
                    <div>
                        <strong>Feriados incompletos — el cálculo puede ser inexacto</strong>
                        <p className="mb-1 mt-1 small">
                            No se encontraron los siguientes feriados en el sistema para el período calculado:
                        </p>
                        <ul className="mb-1 small">
                            {advertenciaFeriados.map(({ anio, tipo }) => (
                                <li key={`${anio}-${tipo}`}>
                                    Feriados <strong>{tipo}</strong> del año <strong>{anio}</strong>
                                </li>
                            ))}
                        </ul>
                        <p className="mb-0 small">
                            Importalos desde <strong>Datos Maestros → Feriados</strong> antes de registrar
                            el vencimiento para asegurar que el plazo sea correcto.
                        </p>
                    </div>
                </div>
            )}

            {/* Observaciones */}
            <div className="mb-4">
                <h6 className="fw-semibold text-primary mb-3">
                    <DescriptionIcon fontSize="small" className="me-2 mb-1" />
                    Observaciones
                </h6>
                <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Detalles sobre la resolución, siniestros con resultado favorable o desfavorable, etc."
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    disabled={cargando}
                    style={{ borderRadius: '0.5rem',
                        border: '1px solid #e0e0e0',
                        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}
                />
                <small className="text-muted mt-1 d-block">
                    Registre aquí el resultado de cada siniestro usando los botones del panel de siniestros (Favorable / Desfavorable).
                </small>
            </div>

            {/* Acciones */}
            <div className="border-top pt-4">
                <p className="text-muted small mb-3">
                    <strong>Seleccioná el destino</strong> según el resultado de los siniestros:
                </p>
                <div className="d-flex justify-content-center flex-wrap gap-3">
                    <button
                        className="btn btn-outline-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => handleAvanzar('Finalizado')}
                        disabled={!puedeAvanzar}
                        title="Todos los siniestros resultaron favorables — el trámite cierra sin juicio"
                    >
                        {cargando ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                            <CheckCircleIcon fontSize="small" />
                        )}
                        Todos favorables → Finalizar
                    </button>

                    <button
                        className="btn btn-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => handleAvanzar('Judicializado')}
                        disabled={!puedeAvanzar}
                        title="Hay siniestros desfavorables — el trámite continúa al poder judicial"
                    >
                        {cargando ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                            <SendIcon fontSize="small" />
                        )}
                        Hay desfavorables → Ir a Juicio
                    </button>
                </div>
                {!fechaNotificacion && (
                    <p className="text-center text-muted small mt-3">
                        Ingresá la fecha de notificación para habilitar las acciones.
                    </p>
                )}
                {fechaNotificacion && cargandoVencimiento && (
                    <p className="text-center text-muted small mt-3">
                        Calculando fecha de vencimiento con feriados...
                    </p>
                )}
            </div>
        </div>
    );
}

export default GestorResolucionSRT;
